/**
 * slotshurra Plinko client — presentation layer.
 *
 * Ported from prototype build 20 (Canvas 2D). Every ball on screen animates a
 * result the server already decided; nothing here touches money except to
 * display it. Balance is always the server's `round.balance`; the round total
 * shown at settlement is the server's `round.win`.
 */
import { PathBank } from './bank.js';
import { PlinkoApi, ApiError } from './api.js';

const BONUS_BEAT      = 450;    // ms after a free ball lands, before the next
const BONUS_FAST_BEAT = 90;     // same, once the player has hit skip
const RELEASE_WINDOW  = 3400;   // ms, whole ball stream
const PLAYBACK        = 0.75;   // 25% slower than the recording (decided Sep 2026)
const RISK_LABEL = { low: 'Low', medium: 'Medium', high: 'High' };
const RISK_COLOR = { low: '#2dd4bf', medium: '#fbbf24', high: '#fb5f7a' };

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const fmt = n => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

export async function mount(root, boot) {
  const $ = id => root.querySelector('#pk-' + id);
  const api = new PlinkoApi(boot);
  const pb = new PathBank(boot.banks);

  let cfg;
  try { cfg = await api.config(); }
  catch (e) { root.innerHTML = `<p class="pk-fatal">Plinko is unavailable right now (${e.message})</p>`; return; }

  const COST = cfg.cost_per_ball, MAX_BALLS = cfg.balls.max;
  const table = (risk, rows) => cfg.paytables[`${risk}_${rows}`];

  /* ------------------------------------------------------------ state */
  const S = {
    risk: 'medium', rows: 14, balls: 10,
    running: false, round: null, bank: null, bankLoading: false,
    active: [], pending: [], ripples: [], flashes: [],
    landed: 0, returned: 0, voided: 0,
    collected: cfg.collected, collectTarget: table('medium', 14).collect_target,
    collectFlash: false, collectFlashUntil: 0, collectFlashTarget: 50,
    bannerUntil: 0, bannerThen: null,
    bonusQueue: [], freeQueue: [], freeTotal: 0, freeDone: 0, bonusWin: 0,
    awaitTap: false, awaitTapUntil: 0,
    timers: [], fastBonus: false, inBonus: false,
    counts: new Int32Array(16),
    balance: cfg.balance, demo: cfg.demo,
    sound: readPref('plinko_sound') === '1', lastTone: 0,
    fair: cfg.fair,
  };

  /* ----------------------------------------------------------- canvas */
  const cv = $('board'), ctx = cv.getContext('2d');
  let GAP = 32, PEG_R = 5, BALL_R = 7, boardW = 544, boardH = 592;
  const pegCache = new Map();
  function pegs() {
    const b = S.bank;
    if (pegCache.has(b.rows)) return pegCache.get(b.rows);
    const out = [], cx = b.board.width / 2;
    for (let r = 0; r < b.rows; r++) {
      const n = r + 3;
      for (let c = 0; c < n; c++) out.push({ x: cx + (c - (n - 1) / 2) * GAP, y: b.board.topY + r * GAP });
    }
    pegCache.set(b.rows, out);
    return out;
  }

  function resize() {
    if (!S.bank) return;
    boardW = S.bank.board.width;
    boardH = S.bank.board.bucketY + 118;          // room below the strip for a miss to fall away
    const cssW = cv.parentElement.clientWidth;
    const maxH = Math.max(320, window.innerHeight - 260);
    const scale = Math.min(cssW / boardW, maxH / boardH);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.style.width = (boardW * scale) + 'px';
    cv.style.height = (boardH * scale) + 'px';
    cv.width = Math.round(boardW * scale * dpr);
    cv.height = Math.round(boardH * scale * dpr);
    ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
  }
  window.addEventListener('resize', resize);

  function bucketRamp(k, rows) {
    const d = Math.abs(k - rows / 2) / (rows / 2);
    if (d < 0.5) { const t = d / 0.5; return `rgb(${lerp(45, 251, t) | 0},${lerp(212, 191, t) | 0},${lerp(191, 36, t) | 0})`; }
    const t = (d - 0.5) / 0.5;
    return `rgb(${lerp(251, 251, t) | 0},${lerp(191, 95, t) | 0},${lerp(36, 122, t) | 0})`;
  }

  function roundRect(x, y, w, h, r) {
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); ctx.fill();
  }

  function draw() {
    const b = S.bank; if (!b) return;
    const rows = b.rows, cx0 = b.board.width / 2, bucketY = b.board.bucketY;
    ctx.clearRect(0, 0, boardW, boardH);

    const P = pegs();
    ctx.fillStyle = '#4d5779';
    for (const p of P) { ctx.beginPath(); ctx.arc(p.x, p.y, PEG_R, 0, 6.2832); ctx.fill(); }

    const now = performance.now();
    ctx.save(); ctx.globalCompositeOperation = 'lighter';
    for (let i = S.ripples.length - 1; i >= 0; i--) {
      const r = S.ripples[i], age = (now - r.t) / 520, p = P[r.peg];
      if (age >= 1 || !p) { S.ripples.splice(i, 1); continue; }   // stale index after a row change
      ctx.globalAlpha = 0.30 * (1 - age); ctx.fillStyle = RISK_COLOR[S.risk];
      ctx.beginPath(); ctx.arc(p.x, p.y, PEG_R + age * 13, 0, 6.2832); ctx.fill();
    }
    ctx.restore();

    // gutter channels down each side
    const stripHalf = (rows / 2 + 0.45) * GAP;
    ctx.fillStyle = 'rgba(0,0,0,.28)';
    for (const sgn of [-1, 1]) { const x = cx0 + sgn * stripHalf; ctx.fillRect(sgn < 0 ? 0 : x, bucketY + 4, sgn < 0 ? x : boardW - x, 44); }

    const grad = ctx.createLinearGradient(0, bucketY + 40, 0, bucketY + 118);
    grad.addColorStop(0, 'rgba(0,0,0,0.30)'); grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad; ctx.fillRect(0, bucketY + 40, boardW, 78);

    // buckets — narrower than the pitch so a slot can open between any two; zone drawn last
    const t = table(S.risk, rows), m = t.multipliers;
    const w = Math.round(GAP * 0.78), y0 = bucketY + 8, h = 32, zoneExtra = 18;
    const order = [];
    for (let k = 0; k <= rows; k++) if (m[k] !== 0) order.push(k);
    for (let k = 0; k <= rows; k++) if (m[k] === 0) order.push(k);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    for (const k of order) {
      const cx = cx0 + (k - rows / 2) * GAP;
      const fl = S.flashes[k] ? clamp((280 - (now - S.flashes[k])) / 280, 0, 1) : 0;
      const isZone = m[k] === 0, th = isZone ? h + zoneExtra : h, ty = (isZone ? y0 - zoneExtra : y0) - fl * 5;
      ctx.fillStyle = isZone ? (S.inBonus ? '#a78bfa' : '#8b7bf0') : bucketRamp(k, rows);
      ctx.globalAlpha = 0.55 + fl * 0.45; roundRect(cx - w / 2, ty, w, th, 5); ctx.globalAlpha = 1;
      ctx.fillStyle = '#0f1320';
      if (isZone) {
        ctx.font = '700 7px ui-sans-serif,system-ui,sans-serif'; ctx.fillText('BONUS', cx, ty + 10);
        if (S.inBonus) {
          ctx.font = '700 13px ui-sans-serif,system-ui,sans-serif';
          ctx.fillText((S.round ? S.round.zonePays : t.zone_pays) + 'x', cx, ty + 30);
        } else {
          const full = S.collectFlash, target = S.collectTarget;
          ctx.font = `700 ${full ? 14 : 13}px ui-sans-serif,system-ui,sans-serif`;
          ctx.fillText(String(full ? target : S.collected), cx, ty + 26);
          ctx.font = '600 8px ui-sans-serif,system-ui,sans-serif'; ctx.fillStyle = 'rgba(15,19,32,.68)';
          ctx.fillText('/' + target, cx, ty + 39);
        }
      } else {
        ctx.font = `600 ${m[k] >= 100 ? 10 : 11}px ui-sans-serif,system-ui,sans-serif`;
        ctx.fillText(m[k] + 'x', cx, ty + h / 2 - 4);
        if (S.counts[k]) { ctx.font = '600 9px ui-sans-serif,system-ui,sans-serif'; ctx.fillStyle = 'rgba(15,19,32,.62)'; ctx.fillText(S.counts[k], cx, ty + h - 8); }
      }
    }

    for (const d of S.active) {
      const lost = d.r.miss || d.r.slot !== null;
      ctx.fillStyle = lost ? '#6b7490' : d.free ? '#fbbf24' : RISK_COLOR[S.risk];
      ctx.globalAlpha = d.alpha * (d.fade === undefined ? 1 : d.fade);
      ctx.beginPath(); ctx.arc(d.x, d.y, BALL_R, 0, 6.2832); ctx.fill();
      ctx.globalAlpha = 0.5; ctx.fillStyle = 'rgba(255,255,255,.55)';
      ctx.beginPath(); ctx.arc(d.x - 2, d.y - 2.5, BALL_R * 0.34, 0, 6.2832); ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  /* ------------------------------------------------------ replay loop */
  let lastT = performance.now();
  function tick(now) {
    const dt = Math.min(now - lastT, 100); lastT = now;   // a backgrounded tab must not teleport
    if (S.bannerUntil && now >= S.bannerUntil) { const then = S.bannerThen; banner(''); if (then) then(); }
    if (S.awaitTap && now >= S.awaitTapUntil) startBonus();  // never leave a round open on an untapped prompt
    if (S.collectFlash && now >= S.collectFlashUntil) { S.collectFlash = false; S.collected = 0; }

    while (S.pending.length && now >= S.pending[0].at) S.active.push(S.pending.shift().drop);

    const b = S.bank;
    if (b) {
      const STEP = 1000 / b.fps / PLAYBACK, cx0 = b.board.width / 2, bucketY = b.board.bucketY;
      for (let n = S.active.length - 1; n >= 0; n--) {
        const d = S.active[n];
        if (d.falling) {                     // phase 2: off the recording, falling away — never re-read the path
          d.fell += dt;
          d.x += (d.targetX - d.x) * Math.min(dt / 150, 1);
          const through = clamp((d.y - (bucketY + 8)) / 32, 0, 1);
          d.y += (dt / 1000) * (170 + 470 * through) * PLAYBACK;
          const stripBottom = bucketY + 40;
          d.fade = d.y < stripBottom + 26 ? 1 : Math.max(0, 1 - (d.y - stripBottom - 26) / 46);
          if (d.y >= bucketY + 112 || d.fade <= 0 || d.fell > 3000) { S.active.splice(n, 1); land(d, now); }
          continue;
        }
        d.acc += dt;                          // phase 1: recorded trajectory, fixed step
        while (d.acc >= STEP && d.frame < d.path.frames - 1) {
          d.frame++; d.acc -= STEP;
          const R = d.path.ripples;
          while (d.ripI < R.length && R[d.ripI] === d.frame) {
            if (S.ripples.length < 24) S.ripples.push({ peg: R[d.ripI + 1], t: now });
            d.ripI += 2;
          }
        }
        const t = d.frame < d.path.frames - 1 ? d.acc / STEP : 1;
        const i = d.frame * 2, j = Math.min(i + 2, d.path.xy.length - 2);
        const laneFade = Math.max(0, 1 - d.frame / 12);
        d.x = lerp(d.path.xy[i], d.path.xy[j], t) + d.lane * laneFade;
        d.y = lerp(d.path.xy[i + 1], d.path.xy[j + 1], t);
        if (d.frame >= d.path.frames - 1) {
          if (d.r.slot !== null) { d.falling = true; d.fell = 0; d.targetX = cx0 + (d.r.slot - b.rows / 2 - 0.5) * GAP; }
          else if (d.r.miss) { d.falling = true; d.fell = 0; d.targetX = cx0 + (d.x < cx0 ? -1 : 1) * (b.rows / 2 + 1.0) * GAP; }
          else { S.active.splice(n, 1); land(d, now); }
        }
      }
    }
    draw();
    requestAnimationFrame(tick);
  }

  function land(d, now) {
    if (d.r.miss || d.r.slot !== null) S.voided++;
    else {
      S.counts[d.r.bucket]++; S.flashes[d.r.bucket] = now; tone(d.r.bucket, S.rows, now);
      if (d.r.zone) S.collected++;
    }
    S.returned += d.r.win;
    if (d.free) S.bonusWin += d.r.win; else S.landed++;
    $('ret').textContent = fmt(S.returned);
    $('ret').style.color = S.returned >= S.round.stake ? 'var(--pk-up)' : 'var(--pk-ink)';
    $('balls').textContent = d.free ? `BONUS ${S.freeDone + 1} / ${S.freeTotal}` : `${S.landed} / ${S.round.balls}`;
    if (d.free) onFreeBallLanded();
    else if (S.landed === S.round.balls) afterPaidBalls();
  }

  /* ------------------------------------------------------------ sound */
  let actx = null;
  function tone(bucket, rows, now) {
    if (!S.sound || now - S.lastTone < 55) return;
    S.lastTone = now;
    try {
      actx = actx || new (window.AudioContext || window.webkitAudioContext)();
      const d = Math.abs(bucket - rows / 2) / (rows / 2), o = actx.createOscillator(), g = actx.createGain();
      o.type = 'triangle'; o.frequency.value = 200 * Math.pow(2, d * 1.6);
      g.gain.setValueAtTime(0.0001, actx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.12, actx.currentTime + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, actx.currentTime + 0.16);
      o.connect(g); g.connect(actx.destination); o.start(); o.stop(actx.currentTime + 0.18);
    } catch { /* audio blocked */ }
  }

  /* ------------------------------------------------------- round flow */
  async function drop() {
    if (S.awaitTap) return startBonus();
    if (S.running) return skip();
    if (!S.bank || S.bankLoading) return;
    if (S.balance < COST) return outOfCredits();

    setControls(false); $('drop').disabled = true; $('drop').textContent = 'Dropping…'; note('');
    let round;
    try {
      round = await api.drop(S.balls, S.rows, S.risk);
    } catch (e) {
      setControls(true); updateDropLabel();
      return roundError(e);
    }
    if (round.rows !== S.rows) { S.rows = round.rows; await loadBank(S.rows); }   // defensive; server echoes what it rolled

    S.running = true; S.round = round; S.landed = 0; S.returned = 0; S.voided = 0;
    S.collected = round.collectedBefore; S.collectTarget = round.collectTarget;
    S.bonusQueue = round.bonuses.slice(); S.freeQueue = []; S.freeDone = 0; S.freeTotal = 0; S.bonusWin = 0;
    S.awaitTap = false; S.bannerThen = null; S.inBonus = false; S.collectFlash = false;
    S.counts = new Int32Array(16); S.flashes = [];
    S.timers.forEach(clearTimeout); S.timers.length = 0; S.fastBonus = false; banner('');
    S.fair = round.fair;
    renderFair();
    $('drop').disabled = false; $('drop').textContent = 'Skip'; $('drop').classList.add('pk-skip');
    $('stake').textContent = fmt(round.stake); $('ret').textContent = '0'; $('ret').style.color = 'var(--pk-ink)';
    $('balls').textContent = `0 / ${round.balls}`;
    S.balance = round.balance;               // already debited and credited server-side; shown at settle

    const stagger = round.balls === 1 ? 0 : clamp(RELEASE_WINDOW / (round.balls - 1), 35, 150);
    const t0 = performance.now();
    round.results.forEach((r, i) => {
      const path = pb.path(round.rows, r.miss ? 'miss' : r.bucket, r.pathId);
      S.pending.push({ at: t0 + i * stagger, drop: { r, path, frame: 0, acc: 0, ripI: 0, fade: 1, falling: false,
        x: path.xy[0], y: path.xy[1], lane: (i % 2 ? 1 : -1) * 5, alpha: 0.88 + (i % 3) * 0.04 } });
    });
  }

  function roundError(e) {
    if (!(e instanceof ApiError)) return note('Something went wrong. Nothing was charged.', true);
    switch (e.code) {
      case 'insufficient_funds': {
        const mb = e.data.max_balls || 0;
        if (e.data.balance !== undefined) S.balance = Number(e.data.balance), renderBalance();
        if (mb >= 1) { note(`Not enough coins for ${S.balls} balls — you can afford ${mb}.`, true); setBalls(mb); }
        else outOfCredits();
        return;
      }
      case 'round_in_flight': return note('A drop is already in progress.', true);
      case 'invalid_balls': return setBalls(S.balls), note(e.message, true);
      case 'invalid_rows': case 'invalid_risk': return note(e.message, true);
      case 'rate_limited': return note('Slow down a moment.', true);
      case 'network': return note(e.message, true);
      default: return note(`${e.message} Nothing was charged.`, true);
    }
  }

  function outOfCredits() {
    if (S.demo) {
      note('Demo credits used up.', true);
      $('cta').innerHTML = `<a class="pk-cta" href="${boot.registerUrl}">Create an account to play for coins</a><button class="pk-link" id="pk-demoreset">Reset demo credits</button>`;
      $('cta').querySelector('#pk-demoreset').onclick = async () => { S.balance = await api.demoReset(); S.collected = 0; renderBalance(); $('cta').innerHTML = ''; note(''); };
    } else {
      note('Out of coins.', true);
      $('cta').innerHTML = `<a class="pk-cta" href="${boot.buyUrl}">Buy coins</a>`;
    }
  }

  function afterPaidBalls() {
    if (S.bonusQueue.length) S.timers.push(setTimeout(armBonus, S.fastBonus ? 120 : 600));
    else settle();
  }
  function armBonus() {
    S.awaitTap = true; S.awaitTapUntil = performance.now() + 30000;
    banner(`${S.bonusQueue[0].length} FREE Balls Bonus Won! Tap to Start`);
    $('drop').textContent = 'Start bonus';
  }
  function startBonus() {
    if (!S.awaitTap) return;
    S.awaitTap = false; banner(''); $('drop').textContent = 'Skip'; runBonus();
  }
  function runBonus() {
    const balls = S.bonusQueue.shift();
    S.inBonus = true;
    S.collectFlash = true; S.collectFlashUntil = performance.now() + (S.fastBonus ? 250 : 1100); S.collectFlashTarget = S.round.collectTarget;
    S.bonusWin = 0; S.freeQueue = balls.slice(); S.freeTotal = balls.length; S.freeDone = 0;
    releaseNextFreeBall();
  }
  function releaseNextFreeBall() {
    const r = S.freeQueue.shift(); if (!r) return;
    const path = pb.path(S.round.rows, r.miss ? 'miss' : r.bucket, r.pathId);
    $('balls').textContent = `BONUS ${S.freeDone + 1} / ${S.freeTotal}`;
    S.active.push({ r, path, frame: 0, acc: 0, ripI: 0, fade: 1, free: true, falling: false, x: path.xy[0], y: path.xy[1], lane: 0, alpha: 1 });
  }
  function onFreeBallLanded() {
    S.freeDone++;
    $('balls').textContent = `BONUS ${S.freeDone} / ${S.freeTotal}`;
    if (S.freeQueue.length) S.timers.push(setTimeout(releaseNextFreeBall, S.fastBonus ? BONUS_FAST_BEAT : BONUS_BEAT));
    else finishBonus();
  }
  function finishBonus() {
    S.inBonus = false;
    banner(`Bonus complete — ${fmt(S.bonusWin)} credits won`, 3000, () => { if (S.bonusQueue.length) armBonus(); else settle(); });
  }

  function banner(text, holdMs, then) {   // expiry is checked in the ticker, never a cancellable timer
    const el = $('banner'); el.textContent = text; el.style.opacity = text ? '1' : '0';
    S.bannerUntil = text && holdMs ? performance.now() + holdMs : 0;
    S.bannerThen = then || null;
  }

  function skip() {
    if (S.awaitTap) return startBonus();
    if (S.bannerThen) { const then = S.bannerThen; banner(''); then(); return; }
    S.fastBonus = true;
    for (const p of S.pending) S.active.push(p.drop);   // drain, never cancel
    S.pending.length = 0;
    for (const d of S.active) { d.frame = d.path.frames - 1; if (d.falling) d.fell = 9999; }
  }

  function settle() {
    const R = S.round;
    S.running = false;
    $('balls').textContent = `${R.balls} / ${R.balls}`;
    S.returned = R.win; $('ret').textContent = fmt(R.win);       // server total, not the JS sum
    S.balance = R.balance; renderBalance();                          // server ledger, never local arithmetic
    S.collected = R.progress;
    const chip = document.createElement('span');
    chip.className = 'pk-chip' + (R.win >= R.stake * 2 ? ' pk-big' : R.win >= R.stake ? ' pk-win' : '');
    chip.textContent = fmt(R.win); chip.title = `${R.balls} balls · ${R.risk}/${R.rows}`;
    const h = $('hist'); h.prepend(chip); while (h.children.length > 12) h.lastChild.remove();
    if (R.capped) note(`Round paid ${fmt(R.win)} — the ${fmt(R.maxWin)} max win per round applied.`, true);
    else if (!R.settled) note('This round is recorded but the coin credit is pending — support can see it.', true);
    else if (R.voided) note(`${R.voided} ball${R.voided > 1 ? 's' : ''} missed the strip and paid nothing.`);
    setControls(true); $('drop').classList.remove('pk-skip'); updateDropLabel();
    if (S.balance < COST) outOfCredits();
  }

  /* --------------------------------------------------------------- UI */
  function note(msg, warn) { const n = $('note'); n.textContent = msg; n.className = 'pk-note' + (warn ? ' pk-warn' : ''); }
  function renderBalance() { $('balance').textContent = fmt(S.balance); }
  function renderFair() {
    if (!S.fair) return;
    $('fair-hash').textContent = S.fair.server_seed_hash;
    $('fair-client').textContent = S.fair.client_seed;
    $('fair-nonce').textContent = S.fair.nonce;
  }
  function setControls(on) {
    root.querySelectorAll('.pk-risk button,.pk-rows button,.pk-presets button,#pk-minus,#pk-plus,#pk-count').forEach(el => el.disabled = !on);
    $('drop').disabled = false;
  }
  function updateDropLabel() {
    $('drop').textContent = S.demo ? `Drop ${S.balls} ${S.balls === 1 ? 'ball' : 'balls'} (demo)` : `Drop ${S.balls} ${S.balls === 1 ? 'ball' : 'balls'}`;
    $('stakebox').textContent = fmt(S.balls * COST);
  }
  function setRisk(v) {
    S.risk = v; root.querySelectorAll('.pk-risk button').forEach(b => b.setAttribute('aria-pressed', b.dataset.v === v));
    if (!S.running) S.collectTarget = table(v, S.rows).collect_target;
    $('void').textContent = (table(v, S.rows).void_rate * 100).toFixed(1) + '% of balls void';
    note('');
  }
  async function loadBank(rows) {
    S.bankLoading = true; $('drop').disabled = true;
    try { S.bank = await pb.load(rows, cfg.bank_version); }
    catch (e) { note(`Board data failed to load (${e.message}). Reload the page.`, true); return; }
    finally { S.bankLoading = false; }
    GAP = S.bank.gap; PEG_R = S.bank.pegRadius; BALL_R = S.bank.ballRadius;
    $('drop').disabled = false; resize();
    for (const r of [rows - 1, rows + 1]) if (r >= 8 && r <= 14) pb.load(r, cfg.bank_version).catch(() => {});   // prefetch neighbours
  }
  async function setRows(v) {
    S.rows = v; root.querySelectorAll('.pk-rows button').forEach(b => b.setAttribute('aria-pressed', +b.dataset.v === v));
    $('buckets').textContent = (v + 1) + ' buckets';
    S.counts = new Int32Array(16); S.ripples.length = 0;   // peg indices belong to the old board
    if (!S.running) S.collectTarget = table(S.risk, v).collect_target;
    setRisk(S.risk);
    await loadBank(v);
  }
  function setBalls(n) {
    S.balls = clamp(Math.round(n) || 1, 1, MAX_BALLS);
    $('count').value = S.balls;
    root.querySelectorAll('.pk-presets button').forEach(b => b.setAttribute('aria-pressed', +b.dataset.v === S.balls));
    updateDropLabel();
  }

  function buildControls() {
    const rowsEl = $('rows');
    for (let r = 8; r <= 14; r++) { const b = document.createElement('button'); b.type = 'button'; b.dataset.v = r; b.textContent = r; b.onclick = () => setRows(r); rowsEl.append(b); }
    const pre = $('presets');
    for (const n of cfg.balls.presets) { const b = document.createElement('button'); b.type = 'button'; b.dataset.v = n; b.textContent = n; b.onclick = () => setBalls(n); pre.append(b); }
    root.querySelectorAll('.pk-risk button').forEach(b => b.onclick = () => setRisk(b.dataset.v));
    $('minus').onclick = () => setBalls(S.balls - 1);
    $('plus').onclick = () => setBalls(S.balls + 1);
    $('count').onchange = e => setBalls(+e.target.value);
    $('count').max = MAX_BALLS;
    $('drop').onclick = drop;
    $('sound').checked = S.sound;
    $('sound').onchange = e => { S.sound = e.target.checked; writePref('plinko_sound', S.sound ? '1' : '0'); };
    cv.onclick = () => { if (S.awaitTap) return startBonus(); if (S.running) skip(); };
    $('fair-toggle').onclick = () => $('fair').classList.toggle('pk-open');
    $('rotate').onclick = async () => {
      if (S.running) return note('Finish the current drop first.', true);
      const seed = $('fair-newclient').value.trim();
      try {
        const r = await api.rotate(seed || undefined);
        S.fair = { server_seed_hash: r.new_server_seed_hash, client_seed: r.client_seed, nonce: 0 }; renderFair();
        $('fair-reveal').innerHTML = `<div>Previous server seed <code>${r.previous_server_seed}</code></div><div>Its hash <code>${r.previous_hash}</code></div><div>Client seed <code>${r.previous_client_seed}</code> · ${r.rounds_played} rounds</div>`;
        $('fair-newclient').value = '';
      } catch (e) { note(e.message, true); }
    };
    $('cost').textContent = COST; $('max').textContent = MAX_BALLS;
    if (S.demo) $('demo').hidden = false;
  }

  buildControls();
  renderBalance(); renderFair();
  setBalls(10); setRisk('medium');
  await setRows(14);
  requestAnimationFrame(tick);
  if (S.balance < COST) outOfCredits();
}

function readPref(k) { try { return localStorage.getItem(k); } catch { return null; } }
function writePref(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode */ } }
